import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { 
  fetchCheckouts, 
  selectCheckoutsState, 
  selectIsUserLoggedIn,
  selectIsAdminLoggedIn
} from '../../../redux/WithcookieSlice';
import { OrderHistoryProps, OrderStatusInfo } from './OrderHistory.types';
import { adaptOrderHistoryProps } from './OrderHistory.adapter';
import ReviewModal from '../../common/ReviewModal';
import SafeImage from '../../common/SafeImage';
import './OrderHistory.css';
import { useTranslation } from '../../../redux/TranslationHelper';

// 아이콘 컴포넌트들
const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const OrderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <path d="M16 10a4 4 0 0 1-8 0"></path>
  </svg>
);

const ShippingIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="3" width="15" height="13"></rect>
    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
    <circle cx="5.5" cy="18.5" r="2.5"></circle>
    <circle cx="18.5" cy="18.5" r="2.5"></circle>
  </svg>
);

const PriceTagIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
    <line x1="7" y1="7" x2="7.01" y2="7"></line>
  </svg>
);

/**
 * PriceDisplay 컴포넌트
 * 가격을 포맷팅하여 표시
 */
const PriceDisplay: React.FC<{ price?: number }> = ({ price = 0 }) => {
  const { t } = useTranslation();
  
  // 숫자를 3자리마다 콤마가 있는 문자열로 변환
  const formattedPrice = useMemo(() => {
    return price?.toLocaleString() || '0';
  }, [price]);
  
  return <span>{formattedPrice}{t('원')}</span>;
};

/**
 * 디바이스 타입을 감지하는 훅
 */
const useDeviceDetect = () => {
  const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 576) {
        setDeviceType('mobile');
      } else if (width < 992) {
        setDeviceType('tablet');
      } else {
        setDeviceType('desktop');
      }
    };
    
    // 초기화
    handleResize();
    
    // 리사이즈 이벤트 리스너 등록
    window.addEventListener('resize', handleResize);
    
    // 클린업
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  return deviceType;
};

/**
 * OrderHistoryComponent
 * 주문 내역을 관리하고 표시하는 컴포넌트
 */
const OrderHistoryComponent: React.FC<any> = (originalProps) => {
  // 어댑터를 통해 props 변환
  const props = useMemo(() => adaptOrderHistoryProps(originalProps), [originalProps]);
  
  const { 
    id,
    style = {},
    className = "order-history-wrapper padding-tb-40",
    componentProps = {},
    isPreview = false,
    itemsPerPage: propItemsPerPage
  } = props;
  
  // 다국어 처리를 위한 훅 사용
  const { t } = useTranslation();
  
  // 컴포넌트 속성에서 값 추출
  const {
    title = t("주문 내역"),
    emptyText = t("주문 내역이 없습니다.")
  } = componentProps;
  
  // 디바이스 타입 감지
  const deviceType = useDeviceDetect();
  const isMobile = deviceType === 'mobile';
  
  // Redux 상태 관리 - 필요한 경우 타입 단언 사용
  const dispatch = useDispatch() as any;
  const { checkouts, loading, error, pagination } = useSelector(selectCheckoutsState);
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const isAdminLoggedIn = useSelector(selectIsAdminLoggedIn);
  
  // 페이지 상태 관리
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(propItemsPerPage || 10); // props에서 itemsPerPage를 가져오거나 기본값 사용
  
  // 리뷰 모달 상태 관리
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  
  // API 호출 중복 방지 ref
  const fetchCheckoutsRef = useRef(false);
  
  // 리뷰 모달 열기 함수 - useCallback 사용
  const openReviewModal = useCallback((productId: number, orderId: number, product: any) => {
    setSelectedProduct(product);
    setSelectedOrder(orderId);
    setReviewModalOpen(true);
  }, []);
  
  // 리뷰 제출 완료 후 호출될 함수 - useCallback 사용
  const handleReviewSubmitted = useCallback(() => {
    // 필요한 경우 추가 로직 (예: 알림 표시)
  }, []);

  // 주문 내역 로드 함수 - useCallback 사용
  const loadOrders = useCallback((page: number, take: number) => {
    // 이미 API 호출 중이면 중복 방지
    if (fetchCheckoutsRef.current) {
      return;
    }
    
    fetchCheckoutsRef.current = true;
    
    dispatch(fetchCheckouts({ page, take }))
      .finally(() => {
        fetchCheckoutsRef.current = false;
      });
  }, [dispatch]);
  
  // 주문 취소 함수 - useCallback 사용
  const handleCancelOrder = useCallback(async (orderId: number) => {
    if (!window.confirm(t('정말로 이 주문을 취소하시겠습니까?'))) {
      return;
    }

    try {
      // TODO: 실제 주문 취소 API 호출
      // await dispatch(cancelOrder(orderId));
      
      // 임시로 알림만 표시 (실제 API 구현 후 제거)
      alert(t('주문이 취소되었습니다.'));
      
      // 주문 목록 새로고침
      loadOrders(currentPage, itemsPerPage);
    } catch (error) {
      console.error('주문 취소 실패:', error);
      alert(t('주문 취소에 실패했습니다. 다시 시도해주세요.'));
    }
  }, [t, currentPage, itemsPerPage, loadOrders]);
  
  // 페이지 변경 핸들러 - useCallback 사용
  const handlePageChange = useCallback((newPage: number) => {
    if (newPage !== currentPage) {
      setCurrentPage(newPage);
      
      // 페이지 상단으로 스크롤
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage]);
  
  // 주문 상태에 따른 텍스트 및 스타일 클래스 반환 - useMemo 사용
  const getStatusInfo = useCallback((status: string): OrderStatusInfo => {
    const statusMap: Record<string, OrderStatusInfo> = {
      'REQUESTED': { text: t('주문 요청됨'), class: 'status-requested', icon: '📋' },
      'PENDING': { text: t('결제 대기 중'), class: 'status-pending', icon: '⏳' },
      'PREPARING': { text: t('상품 준비 중'), class: 'status-preparing', icon: '📦' },
      'PAID': { text: t('결제 완료'), class: 'status-paid', icon: '💰' },
      'SHIPPING': { text: t('배송 중'), class: 'status-shipping', icon: '🚚' },
      'FINISHED': { text: t('배송 완료'), class: 'status-completed', icon: '✅' },
      'CANCELLED': { text: t('주문 취소됨'), class: 'status-cancelled', icon: '❌' },
      'REFUNDED': { text: t('환불 완료'), class: 'status-refunded', icon: '💸' }
    };
    
    return statusMap[status] || { text: status || t('알 수 없음'), class: 'status-unknown', icon: '❓' };
  }, [t]);
  
  // 날짜 포맷 함수 - useCallback 사용
  const formatDate = useCallback((dateString: string): string => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    const today = new Date();
    const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
    const dayOfWeek = weekdays[date.getDay()];
    
    const formattedDate = `${date.getMonth() + 1}/${date.getDate()}(${dayOfWeek})`;
    
    if (date.getFullYear() !== today.getFullYear()) {
      return `${date.getFullYear()}.${formattedDate}`;
    }
    
    return formattedDate;
  }, []);
  
  // 컴포넌트 마운트 시 주문 내역 데이터 로드
  useEffect(() => {
    // 로그인되어 있을 때만 API 호출 (isPreview 조건 제거)
    if (isUserLoggedIn || isAdminLoggedIn) {
      loadOrders(currentPage, itemsPerPage);
    }
  }, [isUserLoggedIn, isAdminLoggedIn, currentPage, itemsPerPage, loadOrders]);
  
  // 페이지네이션 컴포넌트
  const Pagination = useCallback(() => {
    // 페이지네이션 데이터 확인
    const totalPages = pagination?.totalPages || Math.ceil((pagination?.totalItems || checkouts.length) / itemsPerPage) || 1;
    const totalItems = pagination?.totalItems || checkouts.length;
    
    // 페이지 번호가 유효한지 확인
    const validCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
    
    // 표시할 페이지 번호 계산
    let pageNumbers = [];
    let startPage, endPage;
    
    if (deviceType === 'mobile') {
      // 모바일에서는 5개의 페이지 버튼만 표시 (현재 페이지 중심)
      const halfVisible = Math.floor(5 / 2);
      startPage = Math.max(1, validCurrentPage - halfVisible);
      endPage = Math.min(totalPages, startPage + 4);
      
      // 만약 endPage가 totalPages보다 작으면 startPage 조정
      if (endPage < totalPages && endPage - startPage < 4) {
        startPage = Math.max(1, endPage - 4);
      }
    } else {
      // 데스크톱/태블릿에서는 기존 방식 유지
      startPage = Math.max(1, validCurrentPage - 2);
      endPage = Math.min(totalPages, startPage + 4);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    // 페이지 수가 1 이하면 페이지네이션 표시 안함
    if (totalPages <= 1) return null;
    
    return (
      <div className="pagination-container">
        {deviceType !== 'mobile' && (
          <>
            <button 
              className="pagination-button prev" 
              onClick={() => handlePageChange(1)}
              disabled={validCurrentPage === 1}
            >
              {t('처음')}
            </button>
            
            <button 
              className="pagination-button prev" 
              onClick={() => handlePageChange(validCurrentPage - 1)}
              disabled={validCurrentPage === 1}
            >
              {t('이전')}
            </button>
          </>
        )}
        
        {pageNumbers.map(number => (
          <button
            key={number}
            className={`pagination-button ${number === validCurrentPage ? 'active' : ''}`}
            onClick={() => handlePageChange(number)}
          >
            {number}
          </button>
        ))}
        
        {deviceType !== 'mobile' && (
          <>
            <button 
              className="pagination-button next" 
              onClick={() => handlePageChange(validCurrentPage + 1)}
              disabled={validCurrentPage === totalPages}
            >
              {t('다음')}
            </button>
            
            <button 
              className="pagination-button next" 
              onClick={() => handlePageChange(totalPages)}
              disabled={validCurrentPage === totalPages}
            >
              {t('마지막')}
            </button>
          </>
        )}
        
        {deviceType === 'mobile' && (
          <div className="pagination-info">
            {validCurrentPage} / {totalPages} {t('페이지')}
          </div>
        )}
      </div>
    );
  }, [pagination, checkouts.length, itemsPerPage, currentPage, handlePageChange, deviceType, t]);

  // 로딩 상태 표시
  if (loading) {
    return (
      <div className={className} id={id} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="order-history-loading">
                <p>{t('주문 내역을 불러오는 중입니다...')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 에러 표시
  if (error) {
    return (
      <div className={className} id={id} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="order-history-error">
                <p>{t('주문 내역을 불러오는데 실패했습니다')}: {typeof error === 'object' ? (error as any).message || JSON.stringify(error) : error}</p>
                <button 
                  className="order-action-button primary-action"
                  onClick={() => loadOrders(currentPage, itemsPerPage)}
                >
                  {t('다시 시도')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 로그인 필요
  if (!isUserLoggedIn && !isAdminLoggedIn) {
    return (
      <div className={className} id={id} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="order-history-login-required">
                <p>{t('주문 내역을 확인하려면 로그인이 필요합니다.')}</p>
                <Link to="/login" className="order-action-button primary-action">{t('로그인')}</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 주문 내역이 비어있는 경우
  if (!checkouts || (Array.isArray(checkouts) && checkouts.length === 0)) {
    return (
      <div className={className} id={id} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="order-history-empty">
                <h2>{title}</h2>
                <p>{emptyText}</p>
                <Link to="/shop" className="order-action-button secondary-action">
                  {t('쇼핑 계속하기')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 주문 내역이 있는 경우
  return (
    <div className={className} id={id} style={style}>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <h2 className="order-history-title">{title}</h2>
            <p className="order-count">
              {t('총')} {pagination && (pagination.totalItems !== undefined ? pagination.totalItems : checkouts.length)}{t('개의 주문 내역이 있습니다.')}
              {!isMobile && pagination && pagination.totalPages > 1 && 
                ` (${pagination.currentPage}/${pagination.totalPages} ${t('페이지')})`
              }
            </p>
          </div>
        </div>
        
        <div className="order-history-list">
          {checkouts.map((order: any) => {
            const statusInfo = getStatusInfo(order.status);
            const hasMultipleItems = order.carts && order.carts.length > 1;
            
            return (
              <div className="order-item" key={order.id}>
                {/* 모바일에서는 헤더 레이아웃 변경 */}
                {isMobile ? (
                  <div className="order-header mobile-header">
                    <div className="mobile-status-wrapper">
                      <span className={`order-status ${statusInfo.class}`}>
                        <span className="status-icon">{statusInfo.icon}</span> {statusInfo.text}
                      </span>
                      <div className="order-date mobile-date">{statusInfo.text === t('배송 완료') ? `${formatDate(order.createdAt)} ${t('도착')}` : formatDate(order.createdAt)}</div>
                    </div>
                  </div>
                ) : (
                  <div className="order-header">
                    <span className={`order-status ${statusInfo.class}`}>
                      <span className="status-icon">{statusInfo.icon}</span> {statusInfo.text}
                    </span>
                    <span className="order-date">{statusInfo.text === t('배송 완료') ? `${formatDate(order.createdAt)} ${t('도착')}` : formatDate(order.createdAt)}</span>
                  </div>
                )}
                <div className="order-content">
                  {/* 왼쪽: 상품 목록 */}
                  <div className="order-product">
                    {order.carts && order.carts.length > 0 ? (
                      <div className="order-products-list">
                        {order.carts.map((cartItem: any, index: number) => {
                          // 옵션 정보 파싱
                          const optionDisplay = cartItem.options?.options?.map((opt: any) => 
                            `${opt.groupName}: ${opt.valueName}`
                          ).join(', ');
                          
                          return (
                            <div key={cartItem.id} className="order-product-item">
                              <div className="order-product-image">
                                {cartItem.product?.config?.img_url ? (
                                  <img 
                                    src={cartItem.product?.config?.img_url} 
                                    alt={cartItem.product?.title || t("상품")}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={(e) => {
                                      e.currentTarget.src = 'https://via.placeholder.com/100?text=No+Image';
                                    }}
                                  />
                                ) : (
                                  <div style={{ width: '100%', height: '100%', background: '#f3f4f6' }}></div>
                                )}
                              </div>
                              <div className="order-product-info">
                                <div className="order-product-title" title={cartItem.product?.title || t('상품명 없음')}>
                                  {cartItem.product?.title || t('상품명 없음')}
                                </div>
                                {optionDisplay && (
                                  <div className="order-product-option">
                                    {optionDisplay}
                                  </div>
                                )}
                                <div className="order-product-price">
                                  <PriceDisplay 
                                    price={parseInt(cartItem.unitPrice) || cartItem.product?.config?.discounted_price || cartItem.product?.config?.default_price} 
                                  /> 
                                  <span className="order-product-count">× {cartItem.count}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* 주문 총액 표시 */}
                        <div className="order-total-section">
                          <div className="order-total-row">
                            <span className="order-total-label">{t('상품 금액')}</span>
                            <span className="order-total-value">
                              <PriceDisplay price={parseInt(order.parcel_price) || 0} />
                            </span>
                          </div>
                          {order.total_price !== order.parcel_price && (
                            <div className="order-total-row">
                              <span className="order-total-label">{t('배송비')}</span>
                              <span className="order-total-value">
                                <PriceDisplay price={parseInt(order.total_price) - parseInt(order.parcel_price)} />
                              </span>
                            </div>
                          )}
                          <div className="order-total-row order-total-final">
                            <span className="order-total-label">{t('총 결제금액')}</span>
                            <span className="order-total-value">
                              <PriceDisplay price={parseInt(order.total_price) || 0} />
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="empty-products">
                        {t('주문 상품 정보가 없습니다.')}
                      </div>
                    )}
                  </div>
                  
                  {/* 오른쪽: 버튼 영역 */}
                  <div className="order-buttons">
                    <div className="order-actions">
                      {(order.status === "REQUESTED" || order.status === "PREPARING") && (
                        <button 
                          className="order-action-button secondary-action"
                          onClick={() => alert(t('준비 중인 주문입니다.'))}
                        >
                          {t('주문 취소')}
                        </button>
                      )}
                      {order.status === "FINISHED" && (
                        <button 
                          className="order-action-button secondary-action"
                          onClick={() => alert(t('전체 주문에 대한 리뷰 작성 기능은 준비 중입니다.'))}
                        >
                          {t('리뷰 작성')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* 페이지네이션 */}
          {pagination && pagination.totalPages > 0 && (
            <Pagination />
          )}
        </div>
      </div>
      
      {/* 리뷰 모달 */}
      <ReviewModal 
        isOpen={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        productId={selectedProduct?.id}
        orderId={selectedOrder}
        onReviewSubmitted={handleReviewSubmitted}
      />
    </div>
  );
};

export default OrderHistoryComponent; 